import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AUDIENCES = [
  { v: 'customers', l: 'Kunden (Opt-in)' },
  { v: 'leads', l: 'Rapperswil-Voranmeldungen' },
  { v: 'both', l: 'Beide' },
];

const STATUS_MAP = {
  APPROVED: ['Freigegeben', 'bg-green-100 text-green-700 border-green-300'],
  PENDING: ['In Prüfung bei Meta', 'bg-amber-100 text-amber-700 border-amber-300'],
  REJECTED: ['Abgelehnt', 'bg-red-100 text-red-700 border-red-300'],
  NOT_CREATED: ['Noch nicht erstellt', 'bg-gray-100 text-gray-600 border-gray-300'],
  NOT_FOUND: ['Nicht gefunden', 'bg-gray-100 text-gray-600 border-gray-300'],
};

const NewsletterTab = () => {
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterHtml, setNewsletterHtml] = useState('');
  const [newsletterSendEmail, setNewsletterSendEmail] = useState(true);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [resendingFailed, setResendingFailed] = useState(false);
  const [audience, setAudience] = useState('customers');
  const [preview, setPreview] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const [waPreview, setWaPreview] = useState(null);
  const [td, setTd] = useState(null);          // Probetraining-Rabatt-Follow-up
  const [tdBusy, setTdBusy] = useState(false);
  const [tdTest, setTdTest] = useState(null);

  const fetchPreview = useCallback(async (aud) => {
    try {
      const [rEmail, rWa] = await Promise.all([
        axios.get(`${API}/newsletters/recipients-preview?audience=${aud}`, { headers: authHeaders() }),
        axios.get(`${API}/newsletters/rapperswil-whatsapp/preview?audience=${aud}`, { headers: authHeaders() }),
      ]);
      setPreview(rEmail.data);
      setWaPreview(rWa.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadWaStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/newsletters/rapperswil-whatsapp-template/status`, { headers: authHeaders() });
      setWaStatus(res.data);
    } catch (e) { setWaStatus(null); }
  }, []);

  useEffect(() => { fetchPreview(audience); }, [audience, fetchPreview]);
  useEffect(() => { loadWaStatus(); }, [loadWaStatus]);

  const loadTrialDiscount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/trial-discount/settings`, { headers: authHeaders() });
      setTd(res.data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadTrialDiscount(); }, [loadTrialDiscount]);

  const saveTd = async (patch) => {
    setTdBusy(true);
    try {
      await axios.put(`${API}/admin/trial-discount/settings`, patch, { headers: authHeaders() });
      await loadTrialDiscount();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally { setTdBusy(false); }
  };

  const createTdTemplate = async () => {
    setTdBusy(true);
    try {
      const res = await axios.post(`${API}/admin/trial-discount/whatsapp-template/create`, {}, { headers: authHeaders() });
      if (res.data?.success) toast.success('WhatsApp-Template bei Meta eingereicht (Freigabe abwarten)');
      else toast.error('Template konnte nicht erstellt werden: ' + JSON.stringify(res.data?.error || ''));
      await loadTrialDiscount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template-Erstellung fehlgeschlagen');
    } finally { setTdBusy(false); }
  };

  const refreshTdTemplate = async () => {
    setTdBusy(true);
    try {
      await axios.get(`${API}/admin/trial-discount/whatsapp-template/status?refresh=true`, { headers: authHeaders() });
      await loadTrialDiscount();
      toast.success('Status aktualisiert');
    } catch (e) { toast.error('Status-Abfrage fehlgeschlagen'); }
    finally { setTdBusy(false); }
  };

  const testTdRun = async () => {
    setTdBusy(true);
    try {
      const res = await axios.post(`${API}/admin/trial-discount/run?dry_run=true`, {}, { headers: authHeaders() });
      setTdTest(res.data);
      toast.success(`${res.data.eligible} fällige Empfänger gefunden (Testlauf, kein Versand)`);
    } catch (e) { toast.error('Testlauf fehlgeschlagen'); }
    finally { setTdBusy(false); }
  };

  const loadRapperswilDraft = async (aud) => {
    setLoadingDraft(true);
    try {
      const res = await axios.get(`${API}/newsletters/rapperswil-draft?audience=${aud}`, { headers: authHeaders() });
      setNewsletterSubject(res.data.subject);
      setNewsletterHtml(res.data.html_body);
      setAudience(aud);
      setNewsletterSendEmail(true);
      toast.success(
        aud === 'customers'
          ? 'Bestandskunden-Newsletter geladen (ohne Rabatt) — bitte prüfen und senden'
          : 'Voranmeldungen-Newsletter geladen (10% lebenslang) — bitte prüfen und senden'
      );
    } catch (e) {
      toast.error('Vorlage konnte nicht geladen werden');
    } finally {
      setLoadingDraft(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterHtml.trim()) {
      toast.error('Betreff und Inhalt dürfen nicht leer sein');
      return;
    }
    try {
      setSendingNewsletter(true);
      const res = await axios.post(
        `${API}/newsletters/send`,
        { subject: newsletterSubject, html_body: newsletterHtml, channels: ['email'], audience },
        { headers: authHeaders() }
      );
      toast.success(`E-Mail-Versand gestartet an ${res.data.recipients ?? 0} Empfänger (läuft gestaffelt im Hintergrund).`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Newsletter-Versand');
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleResendFailed = async () => {
    try {
      setResendingFailed(true);
      const res = await axios.post(`${API}/newsletters/resend-failed`, {}, { headers: authHeaders() });
      toast.success(res.data.message || 'Erneuter Versand gestartet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erneuter Versand fehlgeschlagen');
    } finally {
      setResendingFailed(false);
    }
  };

  const createWaTemplate = async () => {
    setWaBusy(true);
    try {
      const res = await axios.post(`${API}/newsletters/rapperswil-whatsapp-template/create`, {}, { headers: authHeaders() });
      const okC = res.data?.customers?.success;
      const okL = res.data?.leads?.success;
      if (okC || okL) toast.success('WhatsApp-Templates bei Meta eingereicht (Emoji-Layout) – Freigabe abwarten');
      else toast.error('Templates konnten nicht eingereicht werden');
      await loadWaStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template-Erstellung fehlgeschlagen');
    } finally { setWaBusy(false); }
  };

  const broadcastWa = async () => {
    if (!window.confirm(`WhatsApp-Eröffnung jetzt an ${waPreview?.whatsapp_recipients ?? 0} Empfänger senden?`)) return;
    setWaBusy(true);
    try {
      const res = await axios.post(`${API}/newsletters/rapperswil-whatsapp/broadcast?audience=${audience}`, {}, { headers: authHeaders() });
      toast.success(`WhatsApp-Broadcast gestartet an ${res.data.recipients ?? 0} Empfänger (läuft im Hintergrund).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'WhatsApp-Broadcast fehlgeschlagen');
    } finally { setWaBusy(false); }
  };

  const custTplState = waStatus?.customers?.status || 'NOT_CREATED';
  const leadTplState = waStatus?.leads?.status || 'NOT_CREATED';
  const [custTplLabel, custTplCls] = STATUS_MAP[custTplState] || STATUS_MAP.NOT_CREATED;
  const [leadTplLabel, leadTplCls] = STATUS_MAP[leadTplState] || STATUS_MAP.NOT_CREATED;
  const waCanCreate = [custTplState, leadTplState].some((s) => ['NOT_CREATED', 'NOT_FOUND', 'REJECTED'].includes(s));
  const relevantApproved =
    audience === 'customers' ? custTplState === 'APPROVED'
    : audience === 'leads' ? leadTplState === 'APPROVED'
    : (custTplState === 'APPROVED' || leadTplState === 'APPROVED');

  return (
    <div className="space-y-6" data-testid="newsletter-tab">
      <div className="rounded-lg border border-teal-300 bg-teal-50 p-3 text-sm text-teal-900" data-testid="newsletter-tab-marketing-hint">
        💡 <strong>Neu & zentral:</strong> Kampagnen an Leads, Newsletter-Abonnenten & Kunden (E-Mail + WhatsApp, KI-Vorlagen, Zielgruppen-Auswahl) gibt es jetzt gebündelt unter{' '}
        <a href="/campaigns" className="font-semibold underline">Marketing → „📣 Kampagne senden"</a>. Die Rapperswil-Aktionen unten bleiben hier erhalten.
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">📰 Newsletter senden</CardTitle>
          <CardDescription>
            HTML-Newsletter im StrongFit-Layout an Kunden (mit Opt-in) und/oder Rapperswil-Voranmeldungen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Rapperswil-Eröffnung laden */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2" data-testid="rapperswil-draft-box">
            <div className="text-sm text-amber-900">
              <strong>Rapperswil-Eröffnung:</strong> fertigen Newsletter laden. Bestandskunden erhalten <strong>keinen Rabatt</strong>,
              Voranmeldungen erhalten <strong>10% ein Leben lang</strong> (getrennt versenden).
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => loadRapperswilDraft('customers')} disabled={loadingDraft} data-testid="load-rapperswil-customers-btn">
                {loadingDraft ? 'Lädt…' : 'Bestandskunden laden (ohne Rabatt)'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => loadRapperswilDraft('leads')} disabled={loadingDraft} data-testid="load-rapperswil-leads-btn">
                {loadingDraft ? 'Lädt…' : 'Voranmeldungen laden (10% lebenslang)'}
              </Button>
            </div>
          </div>

          {/* Zielgruppe */}
          <div className="space-y-2">
            <Label>Empfänger</Label>
            <div className="flex flex-col sm:flex-row gap-3">
              {AUDIENCES.map((opt) => (
                <label
                  key={opt.v}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${audience === opt.v ? 'border-black bg-gray-50 font-semibold' : 'border-gray-200'}`}
                  data-testid={`audience-${opt.v}`}
                >
                  <input type="radio" name="nl-audience" value={opt.v} checked={audience === opt.v} onChange={() => setAudience(opt.v)} />
                  {opt.l}
                </label>
              ))}
            </div>
            {preview && (
              <p className="text-xs text-gray-600" data-testid="newsletter-recipient-preview">
                E-Mail-Empfänger: <strong>{preview.email_recipients}</strong>
                {' '}(Kunden: {preview.customer_recipients}, Voranmeldungen: {preview.lead_recipients})
                {' · '}Versand gestaffelt/gedrosselt im Hintergrund.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Betreff *</Label>
            <Input
              value={newsletterSubject}
              onChange={(e) => setNewsletterSubject(e.target.value)}
              placeholder="z.B. Neuigkeiten im StrongFit Studio"
              data-testid="newsletter-subject-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Inhalt (HTML)</Label>
            <textarea
              value={newsletterHtml}
              onChange={(e) => setNewsletterHtml(e.target.value)}
              className="w-full min-h-[180px] border rounded-md p-2 font-mono text-sm"
              placeholder="<h1>Hallo!</h1><p>Hier sind unsere Neuigkeiten...</p>"
              data-testid="newsletter-content-textarea"
            />
            {newsletterHtml && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600">Vorschau anzeigen</summary>
                <iframe
                  title="Newsletter-Vorschau"
                  className="mt-2 w-full border rounded-md bg-white"
                  style={{ height: '640px' }}
                  srcDoc={newsletterHtml}
                  data-testid="newsletter-preview-render"
                />
              </details>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={newsletterSendEmail} onCheckedChange={setNewsletterSendEmail} data-testid="newsletter-email-switch" />
            <span className="text-sm">Per Email senden</span>
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-900">
            Hinweis: Kunden erhalten den Newsletter nur mit aktivem E-Mail-Opt-in. Rapperswil-Voranmeldungen
            erhalten ihn direkt. Der Versand läuft gestaffelt im Hintergrund (gedrosselt, mit automatischer
            Wiederholung) – der Server wird dadurch nicht überlastet. Das Ergebnis siehst du im Versand-Protokoll.
          </div>

          <div className="flex justify-between items-center gap-3">
            <Button
              variant="outline"
              onClick={handleResendFailed}
              disabled={resendingFailed}
              data-testid="resend-failed-newsletter-btn"
              title="Sendet den letzten Newsletter erneut nur an Empfänger, bei denen der Versand fehlgeschlagen ist"
            >
              {resendingFailed ? 'Wird gesendet…' : 'Fehlgeschlagene erneut senden'}
            </Button>
            <Button onClick={handleSendNewsletter} disabled={sendingNewsletter || !newsletterSendEmail} data-testid="send-newsletter-btn">
              {sendingNewsletter ? 'Versende...' : 'Newsletter jetzt senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp-Eröffnung (Rapperswil) */}
      <Card data-testid="wa-opening-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">WhatsApp-Eröffnung (Rapperswil)</CardTitle>
          <CardDescription>
            Proaktiver WhatsApp-Versand braucht von Meta freigegebene Templates. Bestandskunden erhalten die
            Eröffnungs-Info (ohne Rabatt), Voranmeldungen das „10% lebenslang"-Angebot.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span>Bestandskunden-Template:</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${custTplCls}`} data-testid="wa-tpl-customers-badge">{custTplLabel}</span>
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span>Voranmeldungs-Template (10% lebenslang):</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${leadTplCls}`} data-testid="wa-tpl-leads-badge">{leadTplLabel}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {waCanCreate ? (
              <Button size="sm" variant="outline" onClick={createWaTemplate} disabled={waBusy} data-testid="wa-opening-create-btn">
                {waBusy ? 'Sende…' : 'Templates bei Meta erstellen'}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={loadWaStatus} disabled={waBusy} data-testid="wa-opening-refresh-btn">
                Status aktualisieren
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={createWaTemplate} disabled={waBusy} data-testid="wa-opening-resubmit-btn" title="Aktuelles Emoji-Layout bei Meta neu einreichen (Vorlage geht erneut in Prüfung)">
              {waBusy ? 'Sende…' : 'Emoji-Layout neu einreichen'}
            </Button>
            <Button size="sm" onClick={broadcastWa} disabled={!relevantApproved || waBusy} data-testid="wa-opening-broadcast-btn" title={relevantApproved ? '' : 'Erst nach Freigabe durch Meta möglich'}>
              WhatsApp senden ({audience})
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            WhatsApp-Empfänger ({audience}): <strong>{waPreview?.whatsapp_recipients ?? 0}</strong>
            {' · '}Versand gestaffelt im Hintergrund. {relevantApproved ? '' : 'Der Sende-Button ist erst nach Freigabe durch Meta aktiv. Nicht-freigegebene Gruppen werden übersprungen.'}
          </p>
        </CardContent>
      </Card>

      {/* Probetraining-Rabatt Follow-up (Rapperswil, automatisch) */}
      <Card data-testid="td-followup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">Probetraining-Rabatt (Rapperswil, automatisch)</CardTitle>
          <CardDescription>
            24 Std. nach einem Probetraining in Rapperswil bekommt der Kunde automatisch E-Mail + WhatsApp
            mit dem Hinweis „10% Rabatt – ein Leben lang". Läuft täglich um 23:00 im Hintergrund.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(() => {
            const s = td?.settings || {};
            const tpl = td?.whatsapp_template;
            const tplState = tpl?.status || 'NOT_CREATED';
            const [tplLabel, tplCls] = STATUS_MAP[tplState] || STATUS_MAP.NOT_CREATED;
            const tplApproved = tplState === 'APPROVED';
            const tplCanCreate = ['NOT_CREATED', 'NOT_FOUND', 'REJECTED'].includes(tplState);
            return (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!s.enabled} onCheckedChange={(v) => saveTd({ enabled: v })} disabled={tdBusy} data-testid="td-enabled-switch" />
                    <span className="text-sm font-medium">Automatik aktiv</span>
                  </div>
                  <span className="text-xs text-gray-500">Bisher benachrichtigt: <strong>{td?.notified_total ?? 0}</strong></span>
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={s.send_email !== false} onCheckedChange={(v) => saveTd({ send_email: v })} disabled={tdBusy} data-testid="td-email-switch" />
                    Per E-Mail
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={s.send_whatsapp !== false} onCheckedChange={(v) => saveTd({ send_whatsapp: v })} disabled={tdBusy} data-testid="td-whatsapp-switch" />
                    Per WhatsApp
                  </label>
                </div>

                <div className="flex items-center justify-between gap-2 flex-wrap border-t pt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span>WhatsApp-Template:</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${tplCls}`} data-testid="td-template-status">{tplLabel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {tplCanCreate ? (
                      <Button size="sm" variant="outline" onClick={createTdTemplate} disabled={tdBusy} data-testid="td-template-create-btn">
                        {tdBusy ? 'Sende…' : 'Bei Meta erstellen'}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={refreshTdTemplate} disabled={tdBusy} data-testid="td-template-refresh-btn">
                        Status aktualisieren
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={testTdRun} disabled={tdBusy} data-testid="td-test-run-btn">
                      Testlauf (Empfänger prüfen)
                    </Button>
                  </div>
                </div>

                {!tplApproved && s.send_whatsapp !== false && (
                  <p className="text-xs text-amber-700">
                    Hinweis: WhatsApp wird erst versendet, wenn das Template von Meta freigegeben ist. E-Mails gehen unabhängig davon raus.
                  </p>
                )}
                {tdTest && (
                  <div className="text-xs text-gray-600" data-testid="td-test-result">
                    Testlauf: <strong>{tdTest.eligible}</strong> fällige Empfänger (kein Versand ausgelöst).
                  </div>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterTab;
